import { Injectable } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';

/**
 * Admin-v3's design tokens (glass fills, blur, radius, and the light/dark
 * `[data-theme]` switch) are all scoped under `.yl-app` — see
 * src/styles/design-system/_themes.scss — and that class lives on a div
 * inside AdminV3LayoutComponent's template, never on <body>. CDK's default
 * OverlayContainer appends `.cdk-overlay-container` to document.body, which
 * sits outside `.yl-app`, so any CDK-based dialog (AdminV3DialogService)
 * renders with none of the theme's custom properties or `.yl-*` kit CSS in
 * scope — unstyled panel, transparent backdrop.
 *
 * This override appends the container into `.yl-app` instead. It must be
 * provided at the ROOT injector (see app.module.ts), not the admin-v3 route:
 * CDK's `Dialog`/`Overlay` are `providedIn: 'root'` and resolve
 * `OverlayContainer` from the root injector, so a route-level provider is
 * silently ignored. It stays inert for the rest of the app — when `.yl-app`
 * is absent (every non-admin-v3 screen) it falls back to `document.body`,
 * exactly CDK's own default, and nothing outside admin-v3 uses
 * `@angular/cdk/overlay`.
 */
@Injectable()
export class YlOverlayContainer extends OverlayContainer {
    override getContainerElement(): HTMLElement {
        // `.yl-app` is destroyed on navigation away from admin-v3 (and absent
        // if an overlay is ever opened before admin-v3 loads), so the cached
        // container can end up detached or parented to the wrong host.
        // Re-home it whenever the preferred host has changed.
        const preferredHost = this._document.querySelector('.yl-app') ?? this._document.body;
        if (this._containerElement && this._containerElement.parentElement !== preferredHost) {
            this._containerElement.remove();
            this._createContainer();
        }
        return super.getContainerElement();
    }

    protected override _createContainer(): void {
        const container = this._document.createElement('div');
        container.classList.add('cdk-overlay-container');

        const host = this._document.querySelector('.yl-app') ?? this._document.body;
        host.appendChild(container);

        this._containerElement = container;
    }
}
